/** Points-to-grid vertex shader. */

/** @todo Use a better setup than this, for tidier ongoing mapping tests. */
#define test_off 0
#define test_p2g 1
#define test_draw 2
#ifndef test
  #define test test_off
#endif

precision highp float;

points_useSamples
points_useReads_position

/** @todo Draw to all the needed grid passes. */

#ifdef points_splits
  uniform sampler2D points_states[points_splits];
#else
  uniform sampler2D points_states;
#endif

uniform float points_stepNow;
uniform vec4 points_stateShape;

uniform vec4 grid_stateShape;

attribute vec2 vertex;
attribute float index;
attribute float splat;

#if defined(grid_output_motionFull) || defined(grid_output_motionBase)
  /** Advection scheme parameters. */
  uniform float stretch;
  uniform float turn;
#endif

uniform vec4 bounds;
uniform vec2 widths;

#ifdef rescale_mass
  uniform vec2 rescaleMass;
#else
  const float rescaledMass = 1.0;
#endif

#ifdef rescale_affine
  uniform vec2 rescaleAffine;
#else
  const float rescaledAffine = 1.0;
#endif

#if test == test_p2g
  uniform float loop;

  varying vec4 color;
#endif

varying vec2 cellAt;
varying vec2 cellBase;
varying mat3 cellWeights;

#ifdef points_grid_quads
  varying vec2 cellIndex;
#else
  varying vec3 point;
#endif

#if defined(grid_output_mass) || defined(grid_output_motionFull) || \
    defined(grid_output_motionBase)
  varying float mass;
#endif
#if defined(grid_output_motionFull) || defined(grid_output_motionBase)
  varying mat2 affine;
  varying vec2 motion;
#endif
#if defined(grid_output_motionFull)
  varying mat2 stress;
#endif

const vec2 v1 = vec2(1);
const vec2 v0 = v1*0.0;

#pragma glslify: map = require(glsl-map);

#if defined(grid_output_motionFull) || defined(grid_output_motionBase)
  #pragma glslify: transpose = require(glsl-transpose);
#endif

#pragma glslify: indexUV = require(@epok.tech/gl-gpgpu/src/lookup/index-uv);
#pragma glslify: offsetUV = require(@epok.tech/gl-gpgpu/src/lookup/offset-uv);

#ifdef points_grid_quads
  #pragma glslify: spreadUV = require(@epok.tech/gl-gpgpu/src/lookup/spread-uv);
#endif

#pragma glslify: toKernel = require(../kernel/to-kernel);
#pragma glslify: toKernelOffset = require(../kernel/to-offset);

#if defined(rescale_affine)
  #pragma glslify: exponent = require(../vector/exponent);
#endif

/**
 * @todo Handle separate splats, by how many buffers can be drawn to.
 * @todo Separate processes according to active outputs.
 */
void main() {
  vec2 uv = indexUV(index, points_stateShape.xy);

  uv = offsetUV(uv, points_stateShape.xy);
  points_tapState(uv);

  vec2 position = points_data[points_reads_position_position]
    .points_channels_position;

  #if defined(grid_output_mass) || defined(grid_output_motionFull) || \
      defined(grid_output_motionBase)
    // Negative `mass` can indicate points spawning that still affect physics.
    mass = abs(points_data[points_reads_position_mass].points_channels_mass);
  #endif
  #if defined(grid_output_motionFull) || defined(grid_output_motionBase)
    #ifdef rescale_affine
      float rescaledAffine = exponent(rescaleAffine);
    #endif
    #ifdef rescale_mass
      float rescaledMass = exponent(rescaleMass);
    #endif

    vec2 advection = (stretch+vec2(turn, -turn))*0.5;

    affine = rescaledAffine*
      mat2(points_data[points_reads_position_affine].points_channels_affine);

    affine = (mass*rescaledMass)*
      ((affine*advection.x)+(transpose(affine)*advection.y));

    affine /= rescaledAffine;

    motion = points_data[points_reads_position_motion].points_channels_motion;
  #endif
  #if defined(grid_output_motionFull)
    stress = mat2(points_data[points_reads_position_stress]
      .points_channels_stress);
  #endif

  /** Map the position into the correct range with the proper offset. */
  #if test == test_p2g
    color = vec4(uv, 0.5, 1);
    // cellAt = mod((uv+(loop*9e-5))*grid_stateShape.xy, grid_stateShape.xy);
    cellAt = uv*grid_stateShape.xy;
    // gl_Position = vec4(map(uv+(vertex/grid_stateShape.xy), v0, v1, -v1, v1), 0, 1);
    // return;
  #else
    /** Seems the right range, by trial, versus `[0, grid_stateShape.xy-1]`. */
    cellAt = map(position, bounds.xy, bounds.zw, v0, grid_stateShape.xy);
  #endif

  /** Kernel weights and positions, to scale the values per-fragment. */
  toKernel(cellAt, cellBase, cellWeights);

  vec2 to = cellBase+toKernelOffset(vertex*float(kernelSize));

  gl_Position = vec4(map(to, v0, grid_stateShape.xy, -v1, v1), 0, 1);

  #ifdef points_grid_quads
    /**
     * Mask the weights by the distance from these indexes per-fragment.
     * Account for the half-pixel offset at the fragment centre.
     * @see [Pixel coordinates on WebGL Fundamentals](https://webglfundamentals.org/webgl/lessons/webgl-shadertoy.html#pixel-coords)
     */
    cellIndex = spreadUV(vertex, kernelSize);
  #else
    /**
     * Convert vertex position to `gl_FragCoord` window-space.
     * @see [SO](https://stackoverflow.com/a/7158573)
     * @see [SO](https://stackoverflow.com/a/54237532/716898)
     */
    point = vec3(to, 0.5*(gl_PointSize = float(kernelSize)));
  #endif
}
